import { addTokenToUrl, ApiClient } from "@/services/apiClient";
import { type Image } from "@/services/images";
import { type AxiosResponse } from "axios";
import { gettext } from "@/services/translation";
import { GalleryApiError } from "@/services/collection/item/gallery/exceptions";
import { type CollectionItem } from "@/services/collection/item";
import type { AiIdentification } from "@/services/aiIdentification";
import { showErrorToast, showSuccessToast } from "@/services/toasts";

type GenericGalleryResponse =
  | {
      status: "error";
      message: string;
    }
  | {
      status: "ok";
      imageId: number;
    };

type Response_Upload = GenericGalleryResponse;
type Response_Remove = GenericGalleryResponse;
type Response_MakeCover = GenericGalleryResponse;

export const uploadImage = async (file: File, item: CollectionItem, options?: { suppressToast?: boolean }): Promise<Response_Upload> => {
  const url = addTokenToUrl("collection/gallery/upload");
  const data = new FormData();
  data.append("image", file);
  data.append("itemId", item.id.toString());

  return ApiClient.post<Response_Upload>(url, {}, data).then((response) => {
    handleResponse(response.data, gettext.$gettext("Fotka přidána."), options?.suppressToast);
    return response.data;
  });
};

export const setImagesFromAiIdentification = async (item: CollectionItem, identification: AiIdentification): Promise<AxiosResponse> => {
  const url = addTokenToUrl("collection/gallery/set-from-ai-identification");
  const data = {
    identificationId: identification.id,
    itemId: item.id,
  };
  return ApiClient.post(url, {}, data);
};

export const removeImage = async (item: CollectionItem, image: Image, options?: { suppressToast?: boolean }): Promise<Response_Remove> => {
  const url = addTokenToUrl("collection/gallery/remove/" + image.id);
  return ApiClient.get<Response_Remove>(url).then((response) => {
    handleResponse(response.data, gettext.$gettext("Smazal(a) jsi fotku."), options?.suppressToast);
    item.images = item.images.filter((i) => i.id !== image.id);
    return response.data;
  });
};

const requestMakeCover = async (imageId: number): Promise<Response_MakeCover> => {
  const url = addTokenToUrl("collection/gallery/make-cover/" + imageId);
  return ApiClient.get<Response_MakeCover>(url).then(({ data }) => data);
};

export const makeCover = async (item: CollectionItem, newCoverImage: Image): Promise<Response_MakeCover> => {
  const responseData = await requestMakeCover(newCoverImage.id);
  handleResponse(responseData, gettext.$gettext("Nastaveno jako cover."));

  const i = item.images.findIndex((i) => i.id === newCoverImage.id);
  if (i < 0) {
    throw new Error("DEV: Should not happen.");
  }
  const [img] = item.images.splice(i, 1);
  item.images.unshift(img);

  return responseData;
};

/**
 * Forces the gallery's image order to match `imageIdsInOrder` by repeatedly moving images to the
 * front, from last to first. Each "make cover" call is an absolute move-to-front, so calling it in
 * reverse leaves the images in the desired order regardless of the order they landed on the backend
 * in — this also pushes `imageIdsInOrder[0]` ahead of any other image already in the gallery (e.g. a
 * database photo attached via AI identification), even when `imageIdsInOrder` has just one entry.
 */
export async function orderGalleryImages(imageIdsInOrder: number[]): Promise<void> {
  if (imageIdsInOrder.length === 0) return;
  for (let i = imageIdsInOrder.length - 1; i >= 0; i--) {
    await requestMakeCover(imageIdsInOrder[i]);
  }
}

/** Helper to handle old-style api response. */
function handleResponse(response: GenericGalleryResponse, okMsg: string, suppressToast?: boolean): void {
  const status = response.status;
  if (status === "ok") {
    if (!suppressToast) {
      showSuccessToast(okMsg);
    }
  } else {
    const msg = response.message;
    showErrorToast(msg);
    throw new GalleryApiError();
  }
}

export function catchGalleryApiError(e: unknown): void {
  if (e instanceof GalleryApiError) {
    return;
  } else {
    throw e;
  }
}
